import { Product } from '../services/orderLinkAPI';

/**
 * Calcula o preço final de um produto baseado nos atributos selecionados
 */
export const calculateProductPrice = (
  product: Product,
  selectedAttributes: Record<string, any>
): number => {
  let totalPrice = product.price;

  // Percorrer os atributos selecionados e somar os preços extras
  Object.entries(selectedAttributes).forEach(([attributeName, selectedValue]) => {
    const attributeData = product.attributes[attributeName];

    if (!attributeData || typeof attributeData !== 'object') return;

    if (Array.isArray(selectedValue)) {
      // Para checkbox (múltiplos valores)
      selectedValue.forEach((value: string) => {
        if (attributeData[value] && typeof attributeData[value] === 'string') {
          const extraPrice = parseFloat(attributeData[value]);
          if (!isNaN(extraPrice)) {
            totalPrice += extraPrice;
          }
        }
      });
    } else if (selectedValue && typeof selectedValue === 'string') {
      // Para dropdown (valor único)
      if (attributeData[selectedValue] && typeof attributeData[selectedValue] === 'string') {
        const extraPrice = parseFloat(attributeData[selectedValue]);
        if (!isNaN(extraPrice)) {
          totalPrice += extraPrice;
        }
      }
    }
  });

  return totalPrice;
};

/**
 * Obtém os valores padrão para os atributos de um produto
 */
export const getDefaultAttributes = (product: Product): Record<string, any> => {
  const defaultAttributes: Record<string, any> = {};

  if (!product.attributes) {
    return defaultAttributes;
  }

  // Percorrer os atributos do produto diretamente
  Object.entries(product.attributes).forEach(([attributeName, attributeData]) => {
    // Verificar se é um atributo selecionável (objeto com múltiplas opções)
    if (attributeData && typeof attributeData === 'object') {
      const selectableValues = Object.keys(attributeData).filter(key => key !== 'default_value');

      if (selectableValues.length > 1) {
        // É um atributo selecionável
        if (attributeData.default_value) {
          defaultAttributes[attributeName] = attributeData.default_value;
        } else {
          // Usar o primeiro valor disponível como padrão
          defaultAttributes[attributeName] = selectableValues[0];
        }
      }
    }
  });

  return defaultAttributes;
};

/**
 * Verifica se um produto tem atributos configuráveis (selecionáveis)
 */
export const hasSelectableAttributes = (product: Product): boolean => {
  if (!product.attributes) return false;

  return Object.values(product.attributes).some(attributeData => {
    if (!attributeData || typeof attributeData !== 'object') return false;

    // Se é um objeto (não string), consideramos como selecionável
    // Contamos as opções além de default_value e selected
    const selectableValues = Object.keys(attributeData).filter(key => key !== 'default_value' && key !== 'selected');

    // Se tem pelo menos 1 opção e tem default_value, então é selecionável (default + outras opções)
    // Se tem 2 ou mais opções sem default, também é selecionável
    const hasDefault = attributeData.hasOwnProperty('default_value');
    return (hasDefault && selectableValues.length >= 1) || selectableValues.length >= 2;
  });
};

/**
 * Valida se os atributos selecionados são válidos para o produto
 */
export const validateProductAttributes = (
  product: Product,
  selectedAttributes: Record<string, any>
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!product.attributes) {
    return { isValid: true, errors: [] };
  }

  // Validar cada atributo selecionado
  Object.entries(selectedAttributes).forEach(([attributeName, selectedValue]) => {
    const attributeData = product.attributes[attributeName];

    if (attributeData && typeof attributeData === 'object') {
      const availableValues = Object.keys(attributeData).filter(key => key !== 'default_value');

      if (Array.isArray(selectedValue)) {
        // Para checkbox (múltiplos valores)
        selectedValue.forEach((value: string) => {
          if (!availableValues.includes(value)) {
            errors.push(`Valor inválido para ${attributeName}: ${value}`);
          }
        });
      } else if (selectedValue && typeof selectedValue === 'string') {
        // Para dropdown (valor único)
        if (!availableValues.includes(selectedValue)) {
          errors.push(`Valor inválido para ${attributeName}: ${selectedValue}`);
        }
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};
